import { z } from 'zod';

/** ULID: 26 characters, Crockford base32. Ids from a billing provider are not ULIDs and use
 * `providerIdSchema`. */
export const ulidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'must be a ULID');

/**
 * An id minted by whoever handles billing, as carried on a membership.
 *
 * Deliberately loose: it is somebody elses identifier and its shape is theirs to change. The
 * only thing we rely on is that it is a short opaque string safe to put in a URL.
 */
export const providerIdSchema = z
  .string()
  .min(3)
  .max(64)
  .regex(/^[a-zA-Z0-9_]+$/);

/** Unix milliseconds, UTC. Every time in this system is stored and sent this way. */
export const timestampSchema = z.number().int();

export const PLATFORMS = ['windows', 'android'] as const;
export const platformSchema = z.enum(PLATFORMS);
/** A build platform. Linux runs `windows` builds through Proton, it is not its own build target. */
export type Platform = z.infer<typeof platformSchema>;

export const CLIENT_PLATFORMS = ['web', 'windows', 'linux', 'android', 'cli'] as const;
export const clientPlatformSchema = z.enum(CLIENT_PLATFORMS);
export type ClientPlatform = z.infer<typeof clientPlatformSchema>;

/**
 * The header every client sends its `ClientPlatform` in.
 *
 * It lives here because two places have to agree on it: the client sets it on every request and
 * the API has to list it in `Access-Control-Allow-Headers`, or the browser fails the preflight
 * and no request gets through at all.
 */
export const CLIENT_PLATFORM_HEADER = 'X-GB-Platform';

export const ROLES = ['member', 'dev', 'admin'] as const;
export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

export const slugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, numbers and single hyphens');

/** Lowercase hex BLAKE3 digest of a chunk or file. */
export const hashSchema = z.string().regex(/^[0-9a-f]{64}$/, 'must be a 64 character hex hash');

export const cursorSchema = z.string().min(1).max(256);

export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    cursor: z.string().nullable(),
  });
}

export interface Paginated<T> {
  items: T[];
  cursor: string | null;
}

export const publicUserSchema = z.object({
  id: ulidSchema,
  username: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  role: roleSchema,
});
export type PublicUser = z.infer<typeof publicUserSchema>;

export const signedUrlSchema = z.object({
  url: z.string().url(),
  expiresAt: timestampSchema,
});
export type SignedUrl = z.infer<typeof signedUrlSchema>;
